// features/ekip-shift/permissions.ts
//
// Mirror của gate trong `internal/core/ekipshift/service.go`. FE chỉ ẩn/hiện
// control — BE mới là chốt chặn thật. Lệch nhau giữa hai phía cho ra trải
// nghiệm tệ nhất: nút hiện ra rồi bấm vào nhận 403.
import type { Staff } from "@/lib/authv2/store";
import { ACTION } from "@/lib/auth/actions";
import { hasPermission } from "@/lib/authv2/permissions";
import type { EkipDeptGroup } from "./constants";
import { opsRole } from "@/lib/auth/page-access";

type PermUser =
  | Pick<Staff, "id" | "role" | "department" | "skipsWeeklyShift" | "jobTitleCode">
  | null
  | undefined;

/**
 * Khối ảnh. Có cả mã nhóm chung `photo` vì leader khối ảnh thật sự mang mã đó
 * làm department (khác `PHOTO_DEPTS` trong lib/auth/page-access.ts, vốn chỉ
 * liệt kê 3 mã cụ thể). Thiếu `photo` ở đây là khoá luôn leader khỏi trang của
 * chính họ.
 */
const PHOTO_DEPTS = new Set(["photo", "photo_idphoto", "photo_profile", "photo_corporate"]);

/**
 * Ai được tự đăng ký. KHÔNG có mã nhóm `photo`: dòng ca lưu mã phòng cụ thể và
 * không có cách nào đoán leader nhóm muốn mã nào trong ba mã.
 */
const REGISTER_DEPTS = new Set([
  "photo_idphoto",
  "photo_profile",
  "photo_corporate",
  "makeup",
  "sale",
  "cskh",
  "studio_assistant",
]);

/** Vai điều hành toàn studio, không thuộc khối nào cụ thể. */
// branch_manager/area_manager giám sát cả hai khối tại cơ sở/cụm của mình.
// QC KHÔNG vào đây: QC đội nào mang department đội đó nên tự qua nhánh
// PHOTO_DEPTS/makeup bên dưới — QC lễ tân không cần trang ca photo/makeup.
// Vai Điều phối khoá 23/08/2026 (quyết định 0050): trưởng cửa hàng thế chỗ.
// Trưởng ca (QĐ 0425) vào đây qua `opsRole` như Trưởng cửa hàng — gương
// `crossDeptRoles[auth.OpsRole(actor)]` ở máy chủ. Nút TỰ đăng ký vẫn hỏi vai thật.
const CROSS_DEPT_ROLES = new Set([
  "admin",
  "director",
  "branch_manager",
  "area_manager",
]);

export function canReadEkipShifts(user: PermUser): boolean {
  if (!user || !hasPermission(ACTION.EkipShiftRead)) return false;
  if (CROSS_DEPT_ROLES.has(opsRole(user))) return true;
  return (
    PHOTO_DEPTS.has(user.department) ||
    user.department === "makeup" ||
    user.department === "sale" ||
    user.department === "cskh" ||
    user.department === "studio_assistant"
  );
}

/**
 * Chỉ nhân viên thường thuộc các mã phòng cụ thể mới có nút đăng ký. Quản lý
 * các cấp chỉ xem/duyệt/khoá — bản này không cho đăng ký hộ.
 *
 * Vai nhân viên là `staff` với photo/makeup nhưng là `sale` với phòng sale
 * (RBAC canonical tách riêng vai đó), nên phép so phải nhận cả hai. BE không
 * xét vai ở đây — nó chỉ xét department qua `registerDepts` — nên danh sách
 * này là phần chặt hơn của FE, cố ý giữ để quản lý không thấy nút.
 */
const REGISTER_ROLES = new Set(["staff", "sale"]);

export function canRegisterEkipShift(user: PermUser): boolean {
  if (!user || !REGISTER_ROLES.has(user.role)) return false;
  if (skipsWeeklyShift(user)) return false;
  return hasPermission(ACTION.EkipShiftRegister) && REGISTER_DEPTS.has(user.department);
}

/**
 * Người này thuộc mô hình MẶC ĐỊNH CÓ MẶT: không đăng ký ca tuần, chỉ khai
 * ngày nghỉ (cờ `brands.ekip_skips_weekly_shift`, migration 0283).
 *
 * Máy chủ tính sẵn cờ và gửi kèm phiên đăng nhập; ở đây chỉ đọc. Vắng mặt
 * (máy chủ cũ chưa trả trường) đọc thành `false` — giữ nguyên hành vi đang
 * chạy thay vì cắt đường khai ca của người mà ta không biết gì về họ.
 *
 * ⚠️ Cờ này là GỢI Ý HIỂN THỊ, không phải chốt chặn. Chốt thật ở
 * `POST /ekip-shifts` (`ekipshift.Service.Register`), nơi đọc lại CSDL trong
 * chính lời gọi ghi: phiên dựng từ ảnh chụp cũ mang cờ cũ, mà đổi thương hiệu
 * của một người thì không lời gọi nào làm phiên của họ mới lại ngay.
 */
export function skipsWeeklyShift(user: PermUser): boolean {
  return !!user?.skipsWeeklyShift;
}

export function canApproveEkipShift(user: PermUser): boolean {
  return !!user && hasPermission(ACTION.EkipShiftApprove);
}

/** Duyệt được ca thuộc `rowDept` hay không. Mirror `canApproveDept` ở BE. */
export function canApproveDept(user: PermUser, rowDept: string): boolean {
  if (!canApproveEkipShift(user) || !user) return false;
  if (CROSS_DEPT_ROLES.has(opsRole(user))) return true;
  // Ca trợ lý do trưởng cửa hàng trở lên ký (QĐ 0164). Phép so phòng-ban bên
  // dưới sẽ trao quyền đó cho bất kỳ ai mang mã phòng studio_assistant — BE
  // chặn thẳng (canApproveDept ở ekipshift/service.go), nên ở đây phải chặn
  // theo, kẻo nút hiện ra rồi bấm nhận 403.
  if (rowDept === "studio_assistant") return false;
  if (PHOTO_DEPTS.has(user.department) && PHOTO_DEPTS.has(rowDept)) return true;
  return !!user.department && user.department === rowDept;
}

/** Khoá/mở khoá tuần cho cả một nhóm. Mirror `canApproveGroup` ở BE. */
export function canManageGroup(user: PermUser, group: string): boolean {
  if (!canApproveEkipShift(user) || !user) return false;
  if (CROSS_DEPT_ROLES.has(opsRole(user))) return true;
  if (group === "studio_assistant") return false; // xem canApproveDept
  const actorGroup = groupOfDept(user.department);
  return actorGroup !== "" && actorGroup === group;
}

/**
 * Khối của một mã phòng; "" khi mã không thuộc khối nào có roster.
 * Mirror `deptGroup()` ở BE, trừ chỗ BE trả chính mã phòng cho khối lạ.
 */
function groupOfDept(dept: string): EkipDeptGroup | "" {
  if (PHOTO_DEPTS.has(dept)) return "photo";
  if (dept === "makeup") return "makeup";
  if (dept === "sale") return "sale";
  if (dept === "cskh") return "cskh";
  if (dept === "studio_assistant") return "studio_assistant";
  return "";
}

/** Nhóm người dùng được xem. Rỗng = không có quyền vào trang. */
export function readableGroups(user: PermUser): EkipDeptGroup[] {
  if (!canReadEkipShifts(user) || !user) return [];
  if (CROSS_DEPT_ROLES.has(opsRole(user)))
    return ["photo", "makeup", "sale", "cskh", "studio_assistant"];
  const group = groupOfDept(user.department);
  return group === "" ? [] : [group];
}

/**
 * Ai được sửa giờ ca. Mirror `Service.Patch` phía BE.
 *
 * - Leader đúng phòng: sửa mọi ca, mọi trạng thái, kể cả tuần đã khoá.
 * - Chủ ca: chỉ khi ca còn `pending` và tuần chưa khoá.
 *
 * `user.id` ở FE là `staff.id` (authv2 trả staff row), nên phép so này đúng ở
 * đây. Phía BE phải resolve `auth.User.ID` (Casdoor sub) qua bảng staff TRƯỚC
 * khi làm cùng phép so — hai bên khác namespace.
 */
export function canEditShiftTime(
  user: PermUser,
  shift: { staffId: string; department: string; status: string },
  weekLocked: boolean,
): boolean {
  if (!user) return false;
  if (canApproveDept(user, shift.department)) return true;
  if (user.id !== shift.staffId) return false;
  return shift.status === "pending" && !weekLocked;
}

/**
 * Ai được huỷ ca ĐÃ DUYỆT. Mirror `Service.Cancel` phía BE.
 *
 * Chỉ leader đúng phòng, KHÔNG có chủ ca — khác hẳn `canEditShiftTime`. Ca đã
 * duyệt là cam kết có mặt leader đã chốt, và bảng điều phối cửa hàng dựng lên
 * từ đúng những ca đó; người tự gỡ mình khỏi ca ngày mai mà leader chỉ biết
 * khi mở lại lịch là thứ luật này chặn.
 *
 * Ca `pending` trả false: ca đó chưa vào bảng nào cả, chủ ca xoá thẳng bằng
 * `canDeleteShift`.
 */
export function canCancelShift(
  user: PermUser,
  shift: { department: string; status: string },
): boolean {
  if (!user || shift.status !== "approved") return false;
  return canApproveDept(user, shift.department);
}

/**
 * Ai được đổi NGÀY của ca. Cùng luật với đổi giờ (`canEditShiftTime`) vì BE
 * gác cả hai bằng một đường `PATCH` duy nhất — tách hai hàm ở FE sẽ cho ra hai
 * bộ nút lệch nhau mà BE không phân biệt.
 */
export const canEditShiftDate = canEditShiftTime;

/** Xoá ca: chủ ca xoá ca pending của mình, hoặc leader đúng phòng. */
export function canDeleteShift(
  user: PermUser,
  shift: { staffId: string; department: string; status: string },
): boolean {
  if (!user || shift.status !== "pending") return false;
  if (canApproveDept(user, shift.department)) return true;
  return user.id === shift.staffId;
}

/**
 * Ai được NỘP đơn báo nghỉ. Mirror gate `ekipabsence.write` ở route BE.
 *
 * Cố ý KHÔNG lọc thêm theo phòng ban: trang này vốn đã bị `canReadEkipShifts`
 * chặn ở cửa vào, nên người của phòng khác không tới được đây. Thêm rào thứ
 * hai chỉ tạo hai nguồn sự thật cho cùng một câu hỏi.
 *
 * ⚠️ Trưởng cửa hàng và quản lý cụm KHÔNG có quyền này ở BE — họ không nằm
 * trong ê-kíp nên không có ca để nghỉ. Đừng gộp nó với quyền duyệt bên dưới.
 */
export function canRequestAbsence(user: PermUser): boolean {
  return !!user && hasPermission(ACTION.EkipAbsenceWrite);
}

/**
 * Ai NHÌN THẤY mục "Chờ tôi duyệt". Mirror đúng phép so mà BE dùng để nới
 * phạm vi danh sách (`ListAbsences`: không có quyền này thì máy chủ ép
 * `staffId` về chính người gọi).
 *
 * Đây KHÔNG phải quyền bấm Duyệt trên một đơn cụ thể — cửa duyệt còn xét khối
 * của người xin nghỉ, xem `canApproveDept` ở trên (BE `decideAbsence` gọi
 * đúng helper cùng tên). Leader makeup thấy cả đơn của khối ảnh trong danh
 * sách nhưng không quyết được — nên nút phải gác theo từng dòng.
 */
export function canApproveAbsence(user: PermUser): boolean {
  return !!user && hasPermission(ACTION.EkipAbsenceApprove);
}
